import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { 
  Search, 
  Plus, 
  FileDown, 
  Edit2, 
  Trash2, 
  Archive,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Book,
  Users,
  CheckSquare,
  Square,
  ArrowLeft,
  GraduationCap
} from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const ListeNiveauPage = () => {
  const navigate = useNavigate();
  const [niveaux, setNiveaux] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [selectedNiveau, setSelectedNiveau] = useState(null);
  const [formData, setFormData] = useState({
    libelle: '',
    filiere: []
  });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchNiveaux();
    fetchFilieres();
  }, [currentPage, pageSize]);

  const fetchNiveaux = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/niveau/liste-niveaux/?page=${currentPage}&page_size=${pageSize}`);
      
      // Traiter la réponse même si elle est vide
      if (response.data) {
        // Si l'API prend en charge la pagination
        if (response.data.results) {
          setNiveaux(response.data.results || []);
          setTotalPages(Math.ceil((response.data.count || 0) / pageSize));
        } else {
          // Si l'API ne prend pas en charge la pagination
          setNiveaux(response.data || []);
          setTotalPages(Math.ceil((response.data?.length || 0) / pageSize));
        }
      } else {
        setNiveaux([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Erreur:', error);
      
      // Ne pas traiter les 404 comme des erreurs critiques
      if (error.response && error.response.status === 404) {
        setNiveaux([]);
        setTotalPages(1);
      } else {
        toast.error('Erreur lors du chargement des niveaux');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFilieres = async () => {
    try {
      const response = await api.get('/filiere/liste-filieres/');
      
      // Traiter la réponse même si elle est vide
      if (response.data) {
        // Si l'API prend en charge la pagination
        if (response.data.results) {
          setFilieres(response.data.results || []);
        } else {
          setFilieres(response.data || []);
        }
      } else {
        setFilieres([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des filières:', error);
      
      // Ne pas traiter les 404 comme des erreurs critiques
      if (error.response && error.response.status === 404) {
        setFilieres([]);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (selectedNiveau) {
        await api.put(`/niveau/details-niveaux/${selectedNiveau.id}/`, formData);
        toast.success('Niveau mis à jour avec succès');
      } else {
        await api.post('/niveau/liste-niveaux/', formData);
        toast.success('Niveau ajouté avec succès');
      }
      setIsModalOpen(false);
      setFormData({ libelle: '', filiere: [] });
      setSelectedNiveau(null);
      fetchNiveaux();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Une erreur est survenue';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/niveau/details-niveaux/${selectedNiveau.id}/`);
      toast.success('Niveau supprimé avec succès');
      setIsDeleteModalOpen(false);
      setSelectedNiveau(null);
      fetchNiveaux();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleArchive = async () => {
    try {
      // Nous supposons que l'API a une endpoint pour archiver les niveaux
      // similaire à celle des filières
      const updatedData = { ...selectedNiveau, is_archive: true };
      await api.put(`/niveau/api/niveaux/${selectedNiveau.id}/`, updatedData);
      toast.success('Niveau archivé avec succès');
      setIsArchiveModalOpen(false);
      setSelectedNiveau(null);
      fetchNiveaux();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erreur lors de l\'archivage';
      toast.error(errorMessage);
    }
  };

  const handleFiliereChange = (filiereId) => {
    const newFilieres = [...formData.filiere];
    if (newFilieres.includes(filiereId)) {
      // Si la filière est déjà sélectionnée, la retirer
      const index = newFilieres.indexOf(filiereId);
      newFilieres.splice(index, 1);
    } else {
      // Sinon, l'ajouter
      newFilieres.push(filiereId);
    }
    setFormData({ ...formData, filiere: newFilieres });
  };

  // Filtrer les niveaux selon le terme de recherche
  // Filtrer les niveaux en fonction du terme de recherche
  const filteredNiveaux = niveaux.filter(niveau =>
    niveau.libelle.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Obtenir les données paginées si l'API ne le fait pas
  const paginatedNiveaux = filteredNiveaux;

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <GraduationCap className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Gestion des Niveaux</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez les niveaux d'études et leurs spécialités
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-6">
              <div className="max-w-7xl mx-auto">
                {/* Contenu existant */}
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Liste des niveaux</h2>
                  <button
                    onClick={() => {
                      setSelectedNiveau(null);
                      setFormData({ libelle: '', filiere: [] });
                      setIsModalOpen(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Ajouter un niveau
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="relative w-64">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                      placeholder="Rechercher..."
                    />
                  </div>
                </div>

                {loading && niveaux.length === 0 ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
                  </div>
                ) : niveaux.length === 0 ? (
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center">
                    <Book className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun niveau disponible</h3>
                    <p className="text-gray-500 mb-4">Commencez par ajouter un niveau d'études.</p>
                    <button
                      onClick={() => {
                        setSelectedNiveau(null);
                        setFormData({ libelle: '', filiere: [] });
                        setIsModalOpen(true);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Ajouter un niveau
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                      <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Libellé
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Filières associées
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Étudiants
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Statut
                                </th>
                                <th scope="col" className="relative px-6 py-3">
                                  <span className="sr-only">Actions</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {paginatedNiveaux.map((niveau) => (
                                <tr key={niveau.id}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {niveau.libelle}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <div className="flex flex-wrap gap-1">
                                      {niveau.filiere && niveau.filiere.map((fil) => (
                                        <span 
                                          key={fil.id} 
                                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                        >
                                          {fil.libelle}
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      {niveau.nombre_eleves || 0} étudiants
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    {niveau.is_archive ? (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                        Archivé
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        Actif
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex justify-end space-x-2">
                                      <button
                                        onClick={() => {
                                          setSelectedNiveau(niveau);
                                          setFormData({ 
                                            libelle: niveau.libelle, 
                                            filiere: niveau.filiere ? niveau.filiere.map(f => f.id) : [] 
                                          });
                                          setIsModalOpen(true);
                                        }}
                                        className="text-[#202654] hover:text-[#202654]/80"
                                      >
                                        <Edit2 className="h-5 w-5" />
                                      </button>
                                      {!niveau.is_archive ? (
                                        <button
                                          onClick={() => {
                                            setSelectedNiveau(niveau);
                                            setIsArchiveModalOpen(true);
                                          }}
                                          className="text-yellow-600 hover:text-yellow-800"
                                        >
                                          <Archive className="h-5 w-5" />
                                        </button>
                                      ) : (
                                        <button
                                          onClick={() => {
                                            setSelectedNiveau(niveau);
                                            setIsArchiveModalOpen(true);
                                          }}
                                          className="text-green-600 hover:text-green-800"
                                        >
                                          <Archive className="h-5 w-5" />
                                        </button>
                                      )}
                                      <button
                                        onClick={() => {
                                          setSelectedNiveau(niveau);
                                          setIsDeleteModalOpen(true);
                                        }}
                                        className="text-red-600 hover:text-red-800"
                                      >
                                        <Trash2 className="h-5 w-5" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modals existants */}
        {isModalOpen && (
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          {selectedNiveau ? 'Modifier le niveau' : 'Ajouter un niveau'}
                        </h3>
                        <button
                          onClick={() => {
                            setIsModalOpen(false);
                            setSelectedNiveau(null);
                            setFormData({ libelle: '', filiere: [] });
                          }}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <X className="h-6 w-6" />
                        </button>
                      </div>
                      <form onSubmit={handleSubmit} className="mt-4">
                        <div className="mb-4">
                          <label htmlFor="libelle" className="block text-sm font-medium text-gray-700 mb-1">
                            Libellé
                          </label>
                          <input
                            type="text"
                            name="libelle"
                            id="libelle"
                            value={formData.libelle}
                            onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm transition-colors duration-200"
                            placeholder="Entrez le libellé du niveau"
                            required
                          />
                        </div>
                        <div className="mb-4">
                          <div className="flex justify-between items-center mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Filières associées
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                const allFiliereIds = filieres.map(f => f.id);
                                const newSelection = formData.filiere.length === filieres.length ? [] : allFiliereIds;
                                setFormData({ ...formData, filiere: newSelection });
                              }}
                              className="text-sm text-[#202654] hover:text-[#202654]/80 focus:outline-none"
                            >
                              {formData.filiere.length === filieres.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                            </button>
                          </div>
                          <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-2">
                            {filieres.length === 0 ? (
                              <p className="text-gray-500 text-sm">Aucune filière disponible</p>
                            ) : (
                              filieres.map((filiere) => (
                                <div key={filiere.id} className="flex items-center mb-2 last:mb-0">
                                  <button
                                    type="button"
                                    onClick={() => handleFiliereChange(filiere.id)}
                                    className="flex items-center text-gray-700 hover:text-[#202654] focus:outline-none w-full"
                                  >
                                    {formData.filiere.includes(filiere.id) ? (
                                      <CheckSquare className="h-5 w-5 text-[#202654] mr-2" />
                                    ) : (
                                      <Square className="h-5 w-5 text-gray-400 mr-2" />
                                    )}
                                    <span className="text-sm">{filiere.libelle}</span>
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Sélectionnez les filières qui proposeront ce niveau
                          </p>
                        </div>
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                          <button
                            type="submit"
                            disabled={loading}
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                          >
                            {loading ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Enregistrement...
                              </>
                            ) : (
                              selectedNiveau ? 'Modifier' : 'Ajouter'
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setIsModalOpen(false);
                              setSelectedNiveau(null);
                              setFormData({ libelle: '', filiere: [] });
                            }}
                            className="mt-3 w-full inline-flex justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] sm:mt-0 sm:ml-3 sm:w-auto transition-colors duration-200"
                          >
                            Annuler
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmation de suppression */}
        {isDeleteModalOpen && (
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Supprimer le niveau
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Êtes-vous sûr de vouloir supprimer le niveau "{selectedNiveau?.libelle}" ? Cette action est irréversible.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="w-full inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto transition-colors duration-200"
                  >
                    Supprimer
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setSelectedNiveau(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] sm:mt-0 sm:ml-3 sm:w-auto transition-colors duration-200"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmation d'archivage */}
        {isArchiveModalOpen && (
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                      <Archive className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Archiver le niveau
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Êtes-vous sûr de vouloir archiver le niveau "{selectedNiveau?.libelle}" ? Les niveaux archivés ne sont plus disponibles pour les nouvelles inscriptions.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleArchive}
                    className="w-full inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto transition-colors duration-200"
                  >
                    Archiver
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsArchiveModalOpen(false);
                      setSelectedNiveau(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] sm:mt-0 sm:ml-3 sm:w-auto transition-colors duration-200"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ListeNiveauPage;
